import React from 'react';
import {
  Form,
  Input,
  Button,
  message,
  DatePicker,
  Select,
  Typography,
  Space,
  Divider,
  Alert,
} from 'antd';
import { FileTextOutlined, GoogleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import InputNumber from 'antd/es/input-number';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import useDocumentGeneration from '../../hooks/useDocumentGeneration';
import { colors, spacing, radius, typography } from '../../styles/monochromeTheme';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xxl,
  },
  container: {
    maxWidth: '700px',
    margin: '0 auto',
  },
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxl,
  },
  primaryBtn: {
    height: '52px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  secondaryBtn: {
    height: '44px',
    borderRadius: radius.lg,
    background: colors.bgSecondary,
    border: `1px solid ${colors.borderMedium}`,
    color: colors.textSecondary,
  },
  infoBox: {
    padding: spacing.lg,
    backgroundColor: colors.bgTertiary,
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderSubtle}`,
  },
};

function CustomDocumentGenerator() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { generateDoc, loading, googleStatus, authenticateGoogle } = useDocumentGeneration();

  const handleSubmit = async (values) => {
    try {
      const charterId = `CUSTOM-${Date.now()}`;
      const todayDate = dayjs().format('DD/MM/YYYY');

      const customPrice = parseFloat(values.custom_price) || 0;
      const totalPrice = customPrice;

      const documentData = {
        today_date: todayDate,
        charter_date: values.charter_date.format('DD/MM/YYYY'),
        client_name: values.client_name,
        custom_text: values.custom_text || '',
        custom_price: `${customPrice}€`,
        total_price: `${totalPrice}€`,
      };

      await generateDoc(values.document_type, documentData, charterId, 'custom');
      form.resetFields();
    } catch (error) {
      console.error('Error:', error);
      let errorMessage = 'Erreur lors de la génération du document';
      if (error && error.message) errorMessage += ': ' + error.message;
      message.error(errorMessage);
    }
  };

  return (
    <div style={styles.page}>
      <style>
        {`
          .ant-form-item-label > label { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textSecondary} !important; text-transform: uppercase; letter-spacing: 0.5px; }
          .ant-input, .ant-select-selector, .ant-picker, .ant-input-number { border-radius: ${radius.md} !important; border-color: ${colors.borderMedium} !important; }
          .ant-input:focus, .ant-select-selector:focus, .ant-picker-focused { border-color: ${colors.bgDark} !important; box-shadow: 0 0 0 2px rgba(0,0,0,0.1) !important; }
          .ant-divider-inner-text { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textMuted} !important; text-transform: uppercase; letter-spacing: 1px; }
        `}
      </style>

      <div style={styles.container}>
        <div style={styles.card}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: spacing.lg }}>
              <Text strong style={{ fontSize: typography.fontSize.xxl }}>Custom Contract Generator</Text>
              <br />
              <Text type="secondary">Create custom contracts with flexible content</Text>
            </div>

            {/* Google Connection Status */}
            {!googleStatus.authenticated ? (
              <Alert
                message="Connexion Google requise"
                description="Connectez-vous à Google Drive pour pouvoir générer des documents."
                type="warning"
                showIcon
                action={
                  <Button
                    type="primary"
                    icon={<GoogleOutlined />}
                    onClick={authenticateGoogle}
                    style={{ background: '#4285f4', borderColor: '#4285f4' }}
                  >
                    Connecter Google
                  </Button>
                }
                style={{ marginBottom: spacing.lg, borderRadius: radius.lg }}
              />
            ) : (
              <Alert
                message="Google Drive connecté"
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                style={{ marginBottom: spacing.lg, borderRadius: radius.lg }}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                document_type: 'Contrat',
                charter_date: dayjs()
              }}
            >
              <Form.Item
                name="document_type"
                label="Document Type"
                rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}
              >
                <Select size="large">
                  <Option value="Contrat">Contrat Custom</Option>
                  <Option value="Confirmation">Confirmation Custom</Option>
                  <Option value="Facturation">Facturation Custom</Option>
                </Select>
              </Form.Item>

              <Divider>Client Information</Divider>

              <Form.Item
                name="charter_date"
                label="Date"
                rules={[{ required: true, message: 'Requis' }]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} size="large" />
              </Form.Item>

              <Form.Item
                name="client_name"
                label="Client Name"
                rules={[{ required: true, message: 'Requis' }]}
              >
                <Input size="large" placeholder="John Smith" />
              </Form.Item>

              <Divider>Reservation Details</Divider>

              <Form.Item
                name="custom_text"
                label="Description"
                rules={[{ required: true, message: 'Requis' }]}
              >
                <TextArea
                  rows={6}
                  size="large"
                  placeholder="Enter the reservation details here..."
                />
              </Form.Item>

              <Form.Item
                name="custom_price"
                label="Price"
                rules={[{ required: true, message: 'Requis' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  size="large"
                  addonAfter="€"
                />
              </Form.Item>

              <Divider />

              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button
                  onClick={() => navigate('/document-list')}
                  size="large"
                  style={styles.secondaryBtn}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<FileTextOutlined />}
                  size="large"
                  style={styles.primaryBtn}
                >
                  Generate Document
                </Button>
              </Space>
            </Form>

            <Divider />

            <div style={styles.infoBox}>
              <Text strong style={{ display: 'block', marginBottom: spacing.sm }}>Template Variables</Text>
              <Text style={{ fontSize: typography.fontSize.sm, color: colors.textTertiary }}>
                • <code>{'{{today_date}}'}</code>: Current date<br />
                • <code>{'{{charter_date}}'}</code>: Reservation date<br />
                • <code>{'{{client_name}}'}</code>: Client name<br />
                • <code>{'{{custom_text}}'}</code>: Description/details<br />
                • <code>{'{{custom_price}}'}</code>: Price<br />
                • <code>{'{{total_price}}'}</code>: Total price
              </Text>
            </div>
          </Space>
        </div>
      </div>
    </div>
  );
}

export default CustomDocumentGenerator;
